import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Download } from "lucide-react";

const ArtistCV = () => {
  return (
    <div className="py-20">
      {/* Hero Section */}
      <section className="container mx-auto px-4 mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Artist CV
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Comprehensive curriculum vitae of Umar Abdulrasheed, ceramic artist and Creative Director of Terravia Ceramics.
          </p>
        </div>
      </section>

      {/* CV Content */}
      <section className="container mx-auto px-4 mb-16">
        <Card className="max-w-4xl mx-auto p-8 md:p-12">
          {/* Header */}
          <div className="mb-8">
            <h2 className="font-display text-3xl font-bold text-foreground mb-2">Umar Abdulrasheed</h2>
            <p className="text-lg text-muted-foreground mb-4">Founder and Creative Director of Terravia Ceramics</p>
            <p className="text-muted-foreground mb-2">United Kingdom</p>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>Email: info@terravia.com</p>
              <p>Website: terravia.com</p>
              <p>Instagram: @Terravia_Ceramics</p>
              <p>Artsy: <a href="https://www.artsy.net/artist/umar-abdulrasheed" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.artsy.net/artist/umar-abdulrasheed</a></p>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Profile */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Profile</h3>
            <p className="text-muted-foreground leading-relaxed">
              Umar Abdulrasheed is a Nigerian born ceramic artist and the Creative Director of Terravia Ceramics. His work explores the relationship between memory, material, and contemporary ceramic form. Drawing from traditional Nigerian pottery and informed by environmental science, his practice brings together heritage, research, and modern aesthetics. Umar creates sculptural and functional pieces that reflect balance, movement, and the quiet intelligence of clay.
            </p>
          </div>

          <Separator className="my-8" />

          {/* Education */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Education</h3>
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-foreground">MSc Environmental Management</p>
                <p className="text-muted-foreground">University of Hertfordshire, United Kingdom</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">BSc Agricultural Science</p>
                <p className="text-muted-foreground">University of Hertfordshire, United Kingdom</p>
              </div>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Awards */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Awards</h3>
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-foreground">Artist of the Year 2018</p>
                <p className="text-muted-foreground">Kwara State University Departmental Award</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Artist of the Year 2014</p>
                <p className="text-muted-foreground">Kwara State University Centre for Entrepreneurship and Craftsmanship</p>
              </div>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Professional Memberships */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Professional Memberships</h3>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Craft Potters Association United Kingdom</li>
              <li>Friends of Burton at Bideford</li>
            </ul>
          </div>

          <Separator className="my-8" />

          {/* Skills */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Skills</h3>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Ceramic fabrication including wheel thrown and hand built vessels</li>
              <li>Ceramic glaze technology including reduction fired stoneware</li>
              <li>Wheel throwing demonstration and teaching including trimming, slipping, decoration, and handling</li>
              <li>Art and Design resource creation including pedagogical theory and learning materials</li>
              <li>Art and Design research including Material Engagement Theory, craft history, embodiment, Anglo Oriental pottery, New Materialism, and making</li>
              <li>Studio management and exhibition preparation</li>
              <li>Cross cultural collaboration and community engagement</li>
            </ul>
          </div>

          <Separator className="my-8" />

          {/* Work Experience */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Work Experience</h3>
            <div className="space-y-6">
              <div>
                <p className="font-semibold text-foreground">Creative Director, Terravia Ceramics</p>
                <p className="text-sm text-muted-foreground mb-2">Nigeria and United Kingdom, 2019 to present</p>
                <p className="text-muted-foreground">
                  Founder and lead designer of Terravia Ceramics. Responsible for creative direction, material research, glaze development, studio operations, collection planning, and public exhibitions. Leads collaborations, community engagement projects, and contemporary ceramic research.
                </p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Workshop Leader and Social Media Manager, Kwara State University Student Art Hub</p>
                <p className="text-sm text-muted-foreground mb-2">Ilorin, Nigeria, April 2020</p>
                <p className="text-muted-foreground">
                  Led ceramics and mixed media workshops for emerging student artists. Managed digital content, promoted creative events, and produced teaching materials focused on pottery and studio practice.
                </p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Gallery Assistant, Ola Art Gallery</p>
                <p className="text-sm text-muted-foreground mb-2">Lagos, Nigeria, 2021</p>
                <p className="text-muted-foreground">
                  Assisted with exhibition installation, artwork cataloguing, visitor support, and curatorial research. Contributed to artist coordination and audience engagement.
                </p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Curator, Place of Pleasure Exhibition</p>
                <p className="text-sm text-muted-foreground mb-2">Ilorin, Nigeria, 2021</p>
                <p className="text-muted-foreground">
                  Curated a multidisciplinary exhibition exploring material, form, and sensory experience. Coordinated participating artists, managed installation, and produced curatorial writing and press materials.
                </p>
              </div>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Exhibitions */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Exhibitions</h3>
            <div className="space-y-6">
              <div>
                <p className="font-semibold text-foreground mb-2">2025</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>Holyart Exhibition, Brighton, United Kingdom</li>
                  <li>Boomer Gallery Group Exhibition, London, United Kingdom</li>
                  <li>Maidstone Community Art Hub Exhibition, Maidstone, United Kingdom</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-foreground mb-2">2020</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>Kwara State Pottery Fair, Ilorin, Nigeria</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-foreground mb-2">2019</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>Africa Pottery Exhibition, Lagos, Nigeria</li>
                  <li>Shao Art in Clay, Shao, Nigeria</li>
                  <li>Kwara State University Centre for Art and Entrepreneurship Exhibition, Malete, Nigeria</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-foreground mb-2">2018</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>Malete Pottery Festival, Malete, Nigeria</li>
                  <li>Ilorin Innovative Art Hub Exhibition, Ilorin, Nigeria</li>
                </ul>
              </div>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Selected Works */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Selected Works and Collections</h3>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Dual Horizon</li>
              <li>Black Vase</li>
              <li>Earth Toned Lamp</li>
              <li>Balance Series 2024</li>
              <li>Clay and Memory Collection ongoing</li>
            </ul>
          </div>

          <Separator className="my-8" />

          {/* Artist Statement */}
          <div className="mb-8">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Artist Statement</h3>
            <p className="text-muted-foreground leading-relaxed">
              My work is a conversation between tradition and modernity, shaped by memory, the rhythms of nature, and ancestral craft. Each vessel captures movement within stillness, embodying both fragility and strength, and offering a quiet dialogue between the handmade and the natural world. Through Terravia Ceramics, I aim to honour African craftsmanship within a contemporary context while engaging with material research, ecological awareness, and the evolving language of clay.
            </p>
          </div>

          <Separator className="my-8" />

          {/* Download CV */}
          <div className="text-center">
            <h3 className="font-display text-2xl font-semibold text-foreground mb-4">Download CV</h3>
            <Button asChild size="lg" className="gap-2">
              <a href="/Umar_Abdulrasheed_Artistic_CV_2025.pdf" download>
                <Download className="w-5 h-5" />
                Download Full CV (PDF)
              </a>
            </Button>
          </div>
        </Card>
      </section>
    </div>
  );
};

export default ArtistCV;
